import { jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import styles from './vat-message.module.scss.js';
import { clsx } from '../../external/clsx/dist/clsx.js';
import { useLocalization } from '../../hooks/useLocalizations.js';
import { Typography, ErrorBoundary, Link } from '@vp/swan';
import { enabledLocalesForShippingMessage } from '../../constants/locale.js';
import { isValidLocale } from '../../utils/formatter.util.js';
import { shippingUrls } from '../../constants/shippingUrl.js';
import { isVatDisabledForLocale } from '../../utils/price.util.js';

const ShippingMessage = ({ locale }) => {
  const { t } = useLocalization(locale);
  const shippingUrl = shippingUrls[locale.toLowerCase()];
  if (!shippingUrl) {
    return null;
  }
  return /* @__PURE__ */ jsx(Link, { target: "_blank", href: shippingUrl, rel: "noopener noreferrer", "aria-label": t("shipping"), children: t("shipping") }, "shipping-link");
};
const VatMessageInternal = forwardRef(({ className, vatAndShippingConfig, locale, ...rest }, ref) => {
  const { vatInc, hideShippingMessageInVatMessage = false, shippingIncl = false } = vatAndShippingConfig;
  const classNames = clsx(className, styles.vatMessage);
  const { t } = useLocalization(locale);
  const translationKeyForVat = vatInc ? "vatInclusive" : "vatExclusive";
  const translationKeyForShipping = shippingIncl ? vatInc ? "vatAndShipping.includeVatIncludeShipping" : "vatAndShipping.excludeVatIncludeShipping" : vatInc ? "vatAndShipping.includeVatExcludeShipping" : "vatAndShipping.excludeVatExcludeShipping";
  const shippingUrl = shippingUrls[locale.toLowerCase()];
  const shouldRenderShippingMessage = enabledLocalesForShippingMessage.some((loc) => loc.toLowerCase() === locale.toLowerCase()) && !hideShippingMessageInVatMessage && shippingUrl;
  const renderVatMessage = () => {
    if (isVatDisabledForLocale(locale)) {
      return null;
    }
    return shouldRenderShippingMessage ? t(translationKeyForShipping, { shipping: /* @__PURE__ */ jsx(ShippingMessage, { locale }) }) : t(translationKeyForVat);
  };
  return /* @__PURE__ */ jsx(Typography, { as: "span", ref, className: classNames, fontSkin: "body-small", textColor: "subtle", ...rest, children: isValidLocale(locale) && renderVatMessage() });
});
const VatMessage = forwardRef((props, ref) => {
  return /* @__PURE__ */ jsx(
    ErrorBoundary,
    {
      fallback: /* @__PURE__ */ jsx(Typography, { as: "span", fontSkin: "body-small", textColor: "subtle", children: props.placeholdertext }),
      children: /* @__PURE__ */ jsx(VatMessageInternal, { ...props, ref })
    }
  );
});

export { ShippingMessage, VatMessage, VatMessageInternal };
